/**
 * Email Queue API module.
 *
 * Provides endpoints for managing the email queue system including
 * queue entries, stats, cancel/retry actions, and template previews.
 */
import api from './client';
import type { PaginatedResponse } from './client';

export interface EmailQueueEntry {
  id: number;
  recipient_email: string;
  /** The receiving company's name, `''` when unknown. */
  recipient_company_name: string;
  /** The receiving company's type (`customer`, `supplier`), `''` when unknown. */
  recipient_side: string;
  /** The company the email goes out under, `''` for platform mail. */
  customer_name: string;
  /** Truncated to 80 characters; the detail endpoint carries it in full. */
  subject: string;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled' | 'skipped';
  /** What the type filter matches: never blank, unlike `notification_type`. */
  type_key: string;
  notification_type: string;
  priority: string;
  retry_count: number;
  max_retries: number;
  error_message: string;
  created_at: string;
  sent_at: string | null;
  cancelled_at: string | null;
  /** Provider delivery tracking, from the event webhook. */
  delivered_at: string | null;
  opened_at: string | null;
  bounced_at: string | null;
  bounce_reason: string;
}

/** One queue entry with everything the email popup shows. */
export interface EmailQueueEntryDetail extends EmailQueueEntry {
  preheader: string;
  body_preview: string;
  /** The stored body, without the branded wrapper. */
  body_html: string;
  body_text: string;
  /** The whole email as the recipient gets it. */
  rendered_html: string;
  /** Attached file names. */
  attachments: string[];
  batch_id: string;
  next_retry_at: string | null;
  clicked_at: string | null;
  bounce_type: string;
  cancelled_by_email: string | null;
}

export interface EmailQueueStats {
  total: number;
  pending: number;
  sending: number;
  sent_total: number;
  sent_24h: number;
  failed: number;
  cancelled_total: number;
  cancelled: number;
  skipped_total: number;
  skipped: number;
}

/** One value a queue filter can take, with the rows behind it. */
export interface EmailQueueFacet {
  value: string;
  count: number;
  /** Customers only: the company's name. */
  label?: string;
}

export interface EmailQueueFilterOptions {
  types: EmailQueueFacet[];
  customers: EmailQueueFacet[];
  sides: EmailQueueFacet[];
}

export interface EmailTemplateType {
  value: string;
  label: string;
  category: string;
  /** False when no sample context exists, so the type cannot be previewed. */
  has_sample: boolean;
}

/** Whose branding a notification preview was rendered with. */
export interface EmailPreviewBranding {
  /** 'company' = the viewer's own company; 'platform' = Helix staff view. */
  source: 'company' | 'platform';
  company_name: string;
}

export interface EmailTemplatePreviewResponse {
  type: string;
  subject: string;
  html: string;
  branding: EmailPreviewBranding;
}

/** A Reports schedule's email, as listed in Admin Setup > Email Templates. */
export interface ScheduledReportEmail {
  id: number;
  dashboard_id: number;
  dashboard_name: string;
  /** Owning company — only differs between rows for Helix staff. */
  company_name: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  frequency_display: string;
  formats: string[];
  recipient_count: number;
  is_active: boolean;
  /** When the schedule last ran; delivery also depends on the email switches. */
  last_run_at: string | null;
  next_run_at: string | null;
  owner_name: string;
}

export interface ScheduledReportEmailPreviewResponse {
  id: number;
  subject: string;
  /** Branded HTML with the inline charts embedded as data: URIs for the iframe. */
  html: string;
  inline_chart_count: number;
  /** File names that would be attached. */
  attachments: string[];
}

export const emailQueueAPI = {
  /** Fetch paginated queue entries with optional filters. */
  getEntries: (params?: Record<string, string | number | boolean>) =>
    api.get<PaginatedResponse<EmailQueueEntry>>('/admin/email/email-queue/', { params }),

  /** Fetch a single queue entry with its full body. */
  getEntry: (id: number) =>
    api.get<EmailQueueEntryDetail>(`/admin/email/email-queue/${id}/`),

  /**
   * Fetch aggregate stats for the queue dashboard.
   *
   * @param params - The list's filters, status aside, so the counts agree
   *   with the rows shown beneath them.
   */
  getStats: (params?: Record<string, string | number | boolean>) =>
    api.get<EmailQueueStats>('/admin/email/email-queue/stats/', { params }),

  /**
   * The types, customers and recipient sides the queue actually holds.
   *
   * @param params - The list's active filters.
   * @returns Each filter's options, most common first.
   */
  getFilterOptions: (params?: Record<string, string | number | boolean>) =>
    api.get<EmailQueueFilterOptions>('/admin/email/email-queue/filter-options/', { params }),

  /**
   * Download the filtered queue as an Excel workbook.
   *
   * @param params - The list's active filters: the server exports what they select.
   * @returns The .xlsx payload as a Blob.
   */
  exportEntries: (params?: Record<string, string | number | boolean>) =>
    api.get<Blob>('/admin/email/email-queue/export/', { params, responseType: 'blob' }),

  /** Cancel a single pending queue entry. */
  cancelEntry: (id: number) =>
    api.post(`/admin/email/email-queue/${id}/cancel/`),

  /** Cancel all entries in a batch. */
  cancelBatch: (batchId: string) =>
    api.post('/admin/email/email-queue/cancel-batch/', { batch_id: batchId }),

  /** Cancel the pending entries the list's filters select, status aside. */
  cancelAll: (params?: Record<string, string | number | boolean>) =>
    api.post('/admin/email/email-queue/cancel-all/', null, { params }),

  /** Retry a single failed queue entry. */
  retryEntry: (id: number) =>
    api.post(`/admin/email/email-queue/${id}/retry/`),

  /** Retry the failed entries the list's filters select, status aside. */
  retryAll: (params?: Record<string, string | number | boolean>) =>
    api.post('/admin/email/email-queue/retry-all/', null, { params }),

  /** Preview a notification email in the viewer's branding (Helix staff: platform branding). */
  previewTemplate: (type: string) =>
    api.get<EmailTemplatePreviewResponse>('/admin/email/email-templates/preview/', { params: { type } }),

  /** List all available notification template types. */
  listTemplateTypes: () =>
    api.get<Record<string, EmailTemplateType[]>>('/admin/email/email-templates/types/'),

  /** The viewer's company's scheduled report emails (Helix staff: every company's). Unpaginated. */
  listScheduledReportEmails: () =>
    api.get<ScheduledReportEmail[]>('/admin/email/scheduled-report-emails/'),

  /** Render one scheduled report email as it is sent, charts inline. */
  previewScheduledReportEmail: (id: number) =>
    api.get<ScheduledReportEmailPreviewResponse>(`/admin/email/scheduled-report-emails/${id}/preview/`),
};
